import { useState, useEffect } from 'react';
import { Card, Table, Button, Tag, Space, Modal, Form, Select, Input, InputNumber, message, Upload, Tabs, Popconfirm, Image, Alert } from 'antd';
import { PlusOutlined, DeleteOutlined, EditOutlined, UploadOutlined, ReloadOutlined, PictureOutlined } from '@ant-design/icons';

const API_BASE = process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:18080/api';

const { TextArea } = Input;
const APP_KEYS = ['group-image', 'product-video', 'live-script', 'vision-model'];
const APP_LABELS = { 'group-image': '电商组图', 'product-video': '产品视频', 'live-script': '直播话术', 'vision-model': '识图模型（VL视觉）' };
// 模型列表下拉的说明性标签（live-script 列表同时服务：直播话术语言模型 / 组图优化模型 / 视频分镜模型）
const APP_MODEL_LABELS = { 'group-image': '电商组图（图片模型）', 'product-video': '产品视频（视频模型）', 'live-script': '文本模型（话术/优化/分镜共用）', 'vision-model': '识图模型（VL视觉）' };
const APP_TYPES = { 'group-image': 'image', 'product-video': 'video', 'live-script': 'chat', 'vision-model': 'vision' };
// 每个应用可选模型的范围：识图列表只显示视觉模型（vision_support），避免误选纯文本模型
const MODEL_FILTERS = {
  'group-image': m => m.type === 'image',
  'product-video': m => m.type === 'video',
  'live-script': m => m.type === 'chat',
  'vision-model': m => m.type === 'chat' && m.vision_support,
};
const TYPE_LABELS = { image: '图片', video: '视频', chat: '文本', vision: '视觉' };
const TYPE_COLORS = { image: 'orange', video: 'purple', chat: 'blue', vision: 'cyan' };
// 元信息只属于 3 个用户可见应用（vision-model 仅是模型列表分组，不生成应用卡片）
const META_APP_KEYS = ['group-image', 'product-video', 'live-script'];

// 默认封面图（Unsplash 免费图片）
const DEFAULT_COVERS = {
  'group-image': 'https://picsum.photos/seed/groupimage/640/360',
  'product-video': 'https://picsum.photos/seed/video/640/360',
  'live-script': 'https://picsum.photos/seed/livescript/640/360',
};

const api = {
  get: (url, params) => fetch(`${API_BASE}${url}${params ? '?' + new URLSearchParams(params) : ''}`, { headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }).then(r => r.json()),
  post: (url, data) => fetch(`${API_BASE}${url}`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, body: JSON.stringify(data) }).then(r => r.json()),
  del: (url) => fetch(`${API_BASE}${url}`, { method: 'DELETE', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` } }).then(r => r.json()),
  upload: (file) => {
    const fd = new FormData(); fd.append('file', file);
    return fetch(`${API_BASE}/admin/agent-app-metas/upload`, { method: 'POST', headers: { Authorization: `Bearer ${localStorage.getItem('admin_token')}` }, body: fd }).then(r => r.json());
  },
};

export default function AppConfig() {
  const [metas, setMetas] = useState([]);
  const [configs, setConfigs] = useState([]);
  const [allModels, setAllModels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('meta');
  const [metaModal, setMetaModal] = useState(false);
  const [modelModal, setModelModal] = useState(false);
  const [editingMeta, setEditingMeta] = useState(null);
  const [editingConfig, setEditingConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [metaForm] = Form.useForm();
  const [modelForm] = Form.useForm();
  const [selectedApp, setSelectedApp] = useState('group-image');
  const [modelModalAppKey, setModelModalAppKey] = useState('group-image');
  const [coverUploading, setCoverUploading] = useState(false);

  useEffect(() => { fetchMetas(); fetchConfigs(); fetchAllModels(); }, []);

  const fetchMetas = () => {
    api.get('/admin/agent-app-metas').then(res => {
      if (res.code === 0) setMetas(res.data || []);
    }).catch(() => {});
  };

  const fetchConfigs = (appKey) => {
    const key = appKey || selectedApp;
    api.get('/admin/agent-app-configs', { app_key: key }).then(res => {
      if (res.code === 0) setConfigs(res.data || []);
    }).catch(() => {});
  };

  const fetchAllModels = () => {
    api.get('/admin/agent-app-configs/models').then(res => {
      if (res.code === 0) setAllModels(res.data || []);
    }).catch(() => {});
  };

  // Meta CRUD
  const handleEditMeta = (record) => {
    setEditingMeta(record);
    metaForm.setFieldsValue({
      app_key: record.app_key, app_name: record.app_name, short_name: record.short_name,
      description: record.description, cover_url: record.cover_url,
      tags: record.tags, price_label: record.price_label, status: record.status || 'active', sort_order: record.sort_order,
    });
    setMetaModal(true);
  };

  const handleNewMeta = () => {
    setEditingMeta(null);
    metaForm.resetFields();
    metaForm.setFieldsValue({ app_key: 'group-image', sort_order: 1 });
    setMetaModal(true);
  };

  const handleSaveMeta = async () => {
    const values = await metaForm.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    try {
      const payload = { ...values, id: editingMeta?.id || undefined };
      const res = await api.post('/admin/agent-app-metas', payload);
      if (res.code === 0) { message.success('保存成功'); setMetaModal(false); fetchMetas(); }
      else message.error(res.message || '保存失败');
    } finally { setSaving(false); }
  };

  const handleDeleteMeta = async (id) => {
    await api.del(`/admin/agent-app-metas/${id}`);
    message.success('已删除'); fetchMetas();
  };

  const handleApplyDefaultCover = (appKey) => {
    metaForm.setFieldsValue({ cover_url: DEFAULT_COVERS[appKey] || '' });
  };

  // Model Config CRUD
  const handleEditConfig = (record) => {
    setEditingConfig(record);
    setModelModalAppKey(record.app_key);
    modelForm.setFieldsValue({ app_key: record.app_key, model_name: record.model_name, sort_order: record.sort_order, is_default: record.is_default, status: record.status });
    setModelModal(true);
  };

  const handleNewConfig = () => {
    setEditingConfig(null);
    setModelModalAppKey(selectedApp);
    modelForm.resetFields();
    modelForm.setFieldsValue({ app_key: selectedApp, sort_order: 0, is_default: false, status: 'active' });
    setModelModal(true);
  };

  const handleSaveConfig = async () => {
    const values = await modelForm.validateFields().catch(() => null);
    if (!values) return;
    setSaving(true);
    try {
      const payload = { ...values, id: editingConfig?.id || undefined };
      const res = await api.post('/admin/agent-app-configs', payload);
      if (res.code === 0) { message.success('保存成功'); setModelModal(false); fetchConfigs(); }
      else message.error(res.message || '保存失败');
    } finally { setSaving(false); }
  };

  const handleDeleteConfig = async (id) => {
    await api.del(`/admin/agent-app-configs/${id}`);
    message.success('已删除'); fetchConfigs();
  };

  const metaColumns = [
    { title: '应用标识', dataIndex: 'app_key', width: 120, render: v => APP_LABELS[v] || v },
    { title: '封面', dataIndex: 'cover_url', width: 180, render: (v, r) => {
      const src = v || DEFAULT_COVERS[r.app_key];
      return <Image src={src} width={160} height={90} style={{ objectFit: 'cover', borderRadius: 4 }} />;
    }},
    { title: '应用名称', dataIndex: 'app_name', width: 180 },
    { title: '简称', dataIndex: 'short_name', width: 100 },
    { title: '描述', dataIndex: 'description', ellipsis: true },
    { title: '定价标签', dataIndex: 'price_label', width: 100 },
    { title: '状态', dataIndex: 'status', width: 70, render: v => <Tag color={v === 'active' ? 'success' : 'error'}>{v === 'active' ? '启用' : (v || '禁用')}</Tag> },
    { title: '标签', dataIndex: 'tags', width: 150, render: v => (v || '').split(',').filter(Boolean).map(t => <Tag key={t}>{t}</Tag>) },
    {
      title: '操作', width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditMeta(r)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDeleteMeta(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const configColumns = [
    { title: '应用', dataIndex: 'app_key', width: 130, render: v => APP_MODEL_LABELS[v] || APP_LABELS[v] || v },
    { title: '模型', dataIndex: 'model_name', width: 220, render: (v, r) => <div><div style={{ fontWeight: 600 }}>{r.model_config?.display_name || v}</div><div style={{ fontSize: 11, color: '#9ca3af' }}>{v}</div></div> },
    { title: '类型', dataIndex: 'app_key', width: 70, render: v => <Tag color={TYPE_COLORS[APP_TYPES[v]]}>{TYPE_LABELS[APP_TYPES[v]]}</Tag> },
    { title: '价格', dataIndex: 'model_name', width: 170, render: (v, r) => r.model_config ? <span style={{ fontSize: 12, color: '#6b7280' }}>{fmtModelPrice(r.model_config)}</span> : '—' },
    { title: '排序', dataIndex: 'sort_order', width: 60 },
    { title: '默认', dataIndex: 'is_default', width: 60, render: v => v ? <Tag color="blue">默认</Tag> : '-' },
    { title: '状态', dataIndex: 'status', width: 70, render: v => <Tag color={v === 'active' ? 'success' : 'default'}>{v === 'active' ? '启用' : '禁用'}</Tag> },
    {
      title: '操作', width: 160,
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => handleEditConfig(r)}>编辑</Button>
          <Popconfirm title="确定删除？" onConfirm={() => handleDeleteConfig(r.id)}>
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const appOptions = APP_KEYS.map(k => ({ value: k, label: APP_MODEL_LABELS[k] }));
  const metaAppOptions = META_APP_KEYS.map(k => ({ value: k, label: APP_LABELS[k] }));
  const fmtModelPrice = (m) => {
    if (m.billing_type === 'per_image' || m.billing_type === 'per_second') return `¥${(m.input_price || m.billing_rate || 0).toFixed(2)}/${m.billing_type === 'per_image' ? '张' : '秒'}`;
    return `入¥${Number(m.input_price || 0).toFixed(2)}/M · 出¥${Number(m.output_price || 0).toFixed(2)}/M`;
  };
  const modalModelOptions = allModels.filter(MODEL_FILTERS[modelModalAppKey] || (m => true)).map(m => ({ value: m.model_name, label: `${m.display_name || m.model_name} (${m.model_name}) · ${fmtModelPrice(m)}` }));

  return (
    <div style={{ padding: 24 }}>
      <Card title="智能体应用配置" extra={<Button icon={<ReloadOutlined />} onClick={() => { fetchMetas(); fetchConfigs(); fetchAllModels(); }}>刷新</Button>}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={[
          {
            key: 'meta',
            label: '应用信息（封面/名称/定价）',
            children: (
              <>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleNewMeta} style={{ marginBottom: 16 }}>新增应用</Button>
                <Table columns={metaColumns} dataSource={metas} rowKey="id" size="small" pagination={false} locale={{ emptyText: '暂无配置' }} />
              </>
            ),
          },
          {
            key: 'models',
            label: '模型配置（每个应用可选模型）',
            children: (
              <>
                <Alert type="info" showIcon style={{ marginBottom: 16 }} message={
                  <span style={{ fontSize: 12 }}>
                    用户端模型下拉<b>实时从数据库读取</b>此列表。模型官方下架时：点击「编辑」→ 更换「模型」为新的可用模型并保存，用户端下拉立即生效（下次打开页面即更新），无需改代码。<br/>
                    <b>列表与用户端下拉的对应关系</b>：电商组图→图片模型；产品视频→视频模型；文本模型（话术/优化/分镜共用）→ 直播话术的「语言模型」、组图的「优化模型」、视频的「分镜模型」三个下拉共用此列表；识图模型→组图与视频的「识图模型」下拉。<br/>
                    <b>识图模型选型注意</b>：请优先选择直接输出正文的视觉模型（千问VL系列等）；「混合思考」型模型（如 Qwen3.8）默认输出全进思考过程，识图拿不到正文。换模型前建议先用真实商品图测一单。
                  </span>
                } />
                <Space style={{ marginBottom: 16 }}>
                  <span>筛选应用：</span>
                  <Select value={selectedApp} onChange={(v) => { setSelectedApp(v); fetchConfigs(v); }} options={appOptions} style={{ width: 180 }} />
                  <Button type="primary" icon={<PlusOutlined />} onClick={handleNewConfig}>添加模型</Button>
                </Space>
                <Table columns={configColumns} dataSource={configs} rowKey="id" size="small" pagination={false} locale={{ emptyText: '暂无配置，请添加模型' }} />
              </>
            ),
          },
        ]} />
      </Card>

      {/* Meta Edit Modal */}
      <Modal title={editingMeta ? '编辑应用信息' : '新增应用'} open={metaModal} onCancel={() => setMetaModal(false)} onOk={handleSaveMeta} confirmLoading={saving} width={640}>
        <Form form={metaForm} layout="vertical">
          <Form.Item name="app_key" label="应用标识" rules={[{ required: true }]}>
            <Select options={metaAppOptions} disabled={!!editingMeta} />
          </Form.Item>
          <Form.Item name="app_name" label="应用名称" rules={[{ required: true }]}>
            <Input maxLength={100} placeholder="例如：一键生成电商组图" />
          </Form.Item>
          <Form.Item name="short_name" label="简称">
            <Input maxLength={50} placeholder="例如：电商组图" />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <TextArea rows={3} maxLength={500} placeholder="应用功能描述" />
          </Form.Item>
          <Form.Item name="cover_url" label="封面图">
            <Input placeholder="输入URL或上传图片" />
          </Form.Item>
          <Form.Item noStyle shouldUpdate>
            {({ getFieldValue }) => {
              const url = getFieldValue('cover_url') || DEFAULT_COVERS[getFieldValue('app_key')] || '';
              const ak = getFieldValue('app_key');
              return (
                <Space direction="vertical" style={{ marginBottom: 16, width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <Image src={url} width={200} height={112} style={{ objectFit: 'cover', borderRadius: 6, border: '1px solid #e8e8e8' }} fallback={DEFAULT_COVERS[ak]} />
                    <Upload accept="image/*" showUploadList={false} beforeUpload={async f => {
                      const fd = new FormData(); fd.append('file', f);
                      try {
                        const r = await fetch(`${API_BASE}/admin/upload`, { method:'POST', headers:{ Authorization:`Bearer ${localStorage.getItem('admin_token')}` }, body:fd }).then(r=>r.json());
                        if (r.code===0 && r.data?.url) { metaForm.setFieldsValue({ cover_url: r.data.url }); message.success('上传成功'); }
                        else { message.error(r.message||'上传失败'); }
                      } catch(e) { message.error('上传失败'); }
                      return false;
                    }}>
                      <Button icon={<UploadOutlined />}>本地上传</Button>
                    </Upload>
                  </div>
                  <Button size="small" icon={<PictureOutlined />} onClick={() => handleApplyDefaultCover(ak)}>使用默认图</Button>
                </Space>
              );
            }}
          </Form.Item>
          <Form.Item name="tags" label="标签（逗号分隔）">
            <Input maxLength={200} placeholder="例如：图片生成,电商,组图" />
          </Form.Item>
          <Form.Item name="price_label" label="定价标签">
            <Input maxLength={50} placeholder="例如：¥0.60/张" />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{value:'active',label:'启用'},{value:'inactive',label:'禁用'}]} />
          </Form.Item>
          <Form.Item name="sort_order" label="排序">
            <InputNumber min={0} max={99} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Model Config Modal */}
      <Modal title={editingConfig ? '编辑模型配置（可更换模型）' : '添加模型'} open={modelModal} onCancel={() => setModelModal(false)} onOk={handleSaveConfig} confirmLoading={saving} width={620}>
        <Form form={modelForm} layout="vertical">
          {editingConfig && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={<span style={{ fontSize: 12 }}>官方模型下架时，直接更换下方「模型」即可让用户端请求新模型，保存后立即生效。</span>} />}
          <Form.Item name="app_key" label="应用" rules={[{ required: true }]}>
            <Select options={appOptions} disabled={!!editingConfig} />
          </Form.Item>
          <Form.Item name="model_name" label="模型" rules={[{ required: true }]}>
            <Select options={modalModelOptions} showSearch placeholder="选择模型" optionFilterProp="label" />
          </Form.Item>
          <Form.Item name="sort_order" label="排序">
            <InputNumber min={0} max={99} />
          </Form.Item>
          <Form.Item name="is_default" label="设为默认模型" valuePropName="checked">
            <Select options={[{ value: true, label: '是' }, { value: false, label: '否' }]} />
          </Form.Item>
          <Form.Item name="status" label="状态">
            <Select options={[{ value: 'active', label: '启用' }, { value: 'inactive', label: '禁用' }]} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
